/**
 * Requests for the profit-center contractor management endpoints.
 * Mirrors the internal-contractors request style; only the endpoints (and
 * their authorization) differ.
 */

import type {
  CreateContractorResult,
  CreateContractorApiResponse,
  AddContractorUserRequest,
  AddContractorUserResult,
  AddContractorUserApiResponse,
} from "@onsemble/shared/types/internal";
import type {
  CreatePcContractorRequest,
  PcContractorLookupResult,
} from "@onsemble/shared/types/pc-contractors";
import { TanstackError } from "../client";

export const PC_CONTRACTORS_QUERY_KEY = ["/api/pc/contractors"] as const;

/**
 * Resolve a customer number to the full prefixed Hajoca customer id and fetch
 * the account's contact info for form prefill.
 */
export async function lookupPcContractor(
  customerNumber: string,
): Promise<PcContractorLookupResult> {
  const response = await fetch(
    `/api/pc/contractors/lookup?customerNumber=${encodeURIComponent(customerNumber)}`,
    { credentials: "include" },
  );

  const result = await response.json();
  if (!response.ok) {
    throw new TanstackError(
      response.status,
      result?.error || "Failed to look up customer",
      result,
    );
  }
  return result as PcContractorLookupResult;
}

export async function createPcContractor(
  data: CreatePcContractorRequest,
): Promise<CreateContractorResult> {
  const response = await fetch("/api/pc/contractors", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });

  const result: CreateContractorApiResponse = await response.json();
  if (!response.ok || !result.success) {
    throw new TanstackError(
      response.status,
      result.error || "Failed to create contractor",
      result,
    );
  }
  return result.data;
}

export async function addPcContractorUser(
  orgId: string,
  data: AddContractorUserRequest,
): Promise<AddContractorUserResult> {
  const response = await fetch(
    `/api/pc/contractors/${encodeURIComponent(orgId)}/users`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    },
  );

  const result: AddContractorUserApiResponse = await response.json();
  if (!response.ok || !result.success) {
    throw new TanstackError(
      response.status,
      result.error || "Failed to add user",
      result,
    );
  }
  return result.data;
}
